/**
 * product-gallery-sync.js
 *
 * Synchronizes color swatches with the product image gallery (Flickity),
 * reorders gallery images/thumbnails so color images come first, and
 * keeps pagination dots in sync. Pure vanilla JS — no Liquid dependency.
 */
(function () {
  'use strict';

  var INIT_ATTR = 'data-lama-gallery-sequence-v6';


  /* ============================================================
     HELPERS
  ============================================================ */

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }


  function getFlickity(element) {
    if (
      window.Flickity &&
      typeof window.Flickity.data === 'function'
    ) {
      return window.Flickity.data(element);
    }

    if (
      window.theme &&
      window.theme.Flickity &&
      typeof window.theme.Flickity.data === 'function'
    ) {
      return window.theme.Flickity.data(element);
    }

    return null;
  }


  function getMediaId(variant) {
    if (
      variant &&
      variant.featured_media &&
      variant.featured_media.id
    ) {
      return String(variant.featured_media.id);
    }

    return null;
  }


  /* ============================================================
     GET COLOR SWATCHES
  ============================================================ */

  function getColorSwatches(section) {
    var swatches = toArray(
      section.querySelectorAll(
        '.variant__input--color-swatch[data-variant-input]'
      )
    );

    if (swatches.length) {
      return swatches;
    }

    return toArray(
      section.querySelectorAll(
        '.variant-wrapper.color input[type="radio"][data-variant-input], ' +
        '.variant-wrapper.colour input[type="radio"][data-variant-input]'
      )
    );
  }


  /* ============================================================
     GET VARIANT JSON
  ============================================================ */

  function getVariants(section) {
    var jsonElement = section.querySelector(
      '[data-variant-json]'
    );

    if (!jsonElement) {
      return [];
    }

    try {
      var variants = JSON.parse(
        jsonElement.value ||
        jsonElement.textContent ||
        '[]'
      );

      return Array.isArray(variants)
        ? variants
        : [];

    } catch (error) {
      console.error(
        '[LAMA Gallery] Invalid variant JSON.',
        error
      );

      return [];
    }
  }


  /* ============================================================
     BUILD SWATCH -> MEDIA MAP

     Shared packaging / Box images are automatically avoided.
  ============================================================ */

  function buildSwatchMediaIds(
    variants,
    swatches,
    colorOptionKey
  ) {
    var mediaColors = {};


    /*
     * Determine how many colors use every media ID.
     */
    variants.forEach(function (variant) {
      var mediaId = getMediaId(variant);
      var color = variant[colorOptionKey];

      if (
        !mediaId ||
        color == null
      ) {
        return;
      }

      if (!mediaColors[mediaId]) {
        mediaColors[mediaId] = {};
      }

      mediaColors[mediaId][
        String(color)
      ] = true;
    });


    return swatches.map(function (swatch) {
      var color = String(
        swatch.value || ''
      );


      var matches = variants.filter(
        function (variant) {
          return (
            String(
              variant[colorOptionKey] || ''
            ) === color &&
            getMediaId(variant)
          );
        }
      );


      if (!matches.length) {
        return null;
      }


      /*
       * Prefer media belonging only to this color.
       *
       * This prevents the shared Signature Box image
       * becoming the main color image.
       */
      var uniqueColorMatches =
        matches.filter(function (variant) {
          var mediaId =
            getMediaId(variant);

          var colors =
            mediaColors[mediaId];

          return (
            colors &&
            Object.keys(colors).length === 1
          );
        });


      var candidates =
        uniqueColorMatches.length
          ? uniqueColorMatches
          : matches;


      /*
       * Prefer Standard / Regular.
       */
      var preferred =
        candidates.find(function (variant) {
          return (
            variant.options || []
          ).some(function (option) {
            return /^(standard|regular)$/i.test(
              String(option || '').trim()
            );
          });
        })

        ||

        candidates.find(function (variant) {
          return variant.available;
        })

        ||

        candidates[0];


      return getMediaId(preferred);
    });
  }


  /* ============================================================
     DOT SYNCHRONIZATION

     Image 1 -> Dot 1
     Image 2 -> Dot 2
     Image 3 -> Dot 3
     etc.
  ============================================================ */

  function syncDots(
    gallery,
    selectedIndex
  ) {
    var dots = toArray(
      gallery.querySelectorAll(
        '.flickity-page-dots .dot'
      )
    );


    if (!dots.length) {
      return;
    }


    dots.forEach(function (dot, index) {
      var selected =
        index === selectedIndex;


      dot.classList.toggle(
        'is-selected',
        selected
      );


      if (selected) {
        dot.setAttribute(
          'aria-current',
          'step'
        );
      } else {
        dot.removeAttribute(
          'aria-current'
        );
      }


      dot.setAttribute(
        'aria-label',
        'Page dot ' + (index + 1)
      );
    });
  }


  /* ============================================================
     REORDER MAIN PRODUCT GALLERY
  ============================================================ */

  function reorderGallery(
    gallery,
    flkty,
    swatchMediaIds
  ) {
    var cells =
      typeof flkty.getCellElements === 'function'
        ? flkty.getCellElements()
        : toArray(
            gallery.querySelectorAll(
              '.product-main-slide'
            )
          );


    if (!cells.length) {
      return;
    }


    var cellsByMediaId = {};


    cells.forEach(function (cell) {
      if (cell.dataset.mediaId) {
        cellsByMediaId[
          String(cell.dataset.mediaId)
        ] = cell;
      }
    });


    var orderedCells = [];


    /*
     * COLOR IMAGES FIRST
     * according to swatch order.
     */
    swatchMediaIds.forEach(function (mediaId) {
      if (!mediaId) {
        return;
      }


      var cell =
        cellsByMediaId[
          String(mediaId)
        ];


      if (
        cell &&
        orderedCells.indexOf(cell) === -1
      ) {
        orderedCells.push(cell);
      }
    });


    /*
     * All remaining media goes afterwards:
     *
     * Box
     * Packaging
     * Lifestyle
     * Video
     * Extra images
     */
    cells.forEach(function (cell) {
      if (
        orderedCells.indexOf(cell) === -1
      ) {
        orderedCells.push(cell);
      }
    });


    var needsReorder =
      orderedCells.some(
        function (cell, index) {
          return cells[index] !== cell;
        }
      );


    if (needsReorder) {

      /*
       * Preferred method:
       * use Flickity's own cell APIs.
       */
      if (
        typeof flkty.remove === 'function' &&
        typeof flkty.append === 'function'
      ) {

        flkty.remove(cells);

        flkty.append(
          orderedCells
        );

      } else {

        /*
         * Fallback.
         */
        var slider =
          gallery.querySelector(
            '.flickity-slider'
          );


        if (slider) {
          orderedCells.forEach(
            function (cell) {
              slider.appendChild(cell);
            }
          );
        }


        if (
          typeof flkty.reloadCells ===
          'function'
        ) {
          flkty.reloadCells();
        }
      }
    }


    /*
     * Get final Flickity order.
     */
    var finalCells =
      typeof flkty.getCellElements === 'function'
        ? flkty.getCellElements()
        : orderedCells;


    /*
     * Synchronize theme indexes.
     */
    finalCells.forEach(function (cell, index) {
      cell.dataset.index =
        String(index);


      var photoSwipeImage =
        cell.querySelector(
          '[data-photoswipe-src][data-index]'
        );


      if (photoSwipeImage) {
        photoSwipeImage.dataset.index =
          String(index + 1);
      }
    });


    if (
      typeof flkty.resize === 'function'
    ) {
      flkty.resize();
    }


    if (
      typeof flkty.reposition === 'function'
    ) {
      flkty.reposition();
    }
  }


  /* ============================================================
     REORDER DESKTOP THUMBNAILS
  ============================================================ */

  function reorderThumbnails(
    section,
    swatchMediaIds
  ) {
    var scroller =
      section.querySelector(
        '[data-product-thumbs] ' +
        '.product__thumbs--scroller'
      );


    if (!scroller) {
      return;
    }


    var items = toArray(
      scroller.querySelectorAll(
        '.product__thumb-item'
      )
    );


    if (!items.length) {
      return;
    }


    var itemsByMediaId = {};


    items.forEach(function (item) {
      var thumb =
        item.querySelector(
          '[data-product-thumb]'
        );


      if (
        thumb &&
        thumb.dataset.id
      ) {
        itemsByMediaId[
          String(thumb.dataset.id)
        ] = item;
      }
    });


    var ordered = [];


    /*
     * Color thumbnails first.
     */
    swatchMediaIds.forEach(function (mediaId) {
      if (!mediaId) {
        return;
      }


      var item =
        itemsByMediaId[
          String(mediaId)
        ];


      if (
        item &&
        ordered.indexOf(item) === -1
      ) {
        ordered.push(item);
      }
    });


    /*
     * Extra thumbnails afterwards.
     */
    items.forEach(function (item) {
      if (
        ordered.indexOf(item) === -1
      ) {
        ordered.push(item);
      }
    });


    ordered.forEach(function (item, index) {
      scroller.appendChild(item);


      item.dataset.index =
        String(index);


      var thumb =
        item.querySelector(
          '[data-product-thumb]'
        );


      if (thumb) {
        thumb.dataset.index =
          String(index);
      }
    });
  }


  /* ============================================================
     INITIALIZE ONE PRODUCT
  ============================================================ */

  function initProduct(
    section,
    attempt
  ) {
    attempt = attempt || 0;


    if (!section) {
      return;
    }


    if (
      section.getAttribute(
        INIT_ATTR
      ) === 'true'
    ) {
      return;
    }


    var gallery =
      section.querySelector(
        '[data-product-photos]'
      );


    var swatches =
      getColorSwatches(section);


    var variants =
      getVariants(section);


    if (
      !gallery ||
      !swatches.length ||
      !variants.length
    ) {
      return;
    }


    var flkty =
      getFlickity(gallery);


    /*
     * Wait for theme Flickity initialization.
     */
    if (!flkty) {

      if (attempt < 60) {
        setTimeout(
          function () {
            initProduct(
              section,
              attempt + 1
            );
          },
          100
        );
      }

      return;
    }


    section.setAttribute(
      INIT_ATTR,
      'true'
    );


    var colorOptionKey =
      swatches[0].getAttribute(
        'data-index'
      ) || 'option1';


    var swatchMediaIds =
      buildSwatchMediaIds(
        variants,
        swatches,
        colorOptionKey
      );


    /* ==========================================================
       ORDER GALLERY + THUMBNAILS
    ========================================================== */

     /* ==========================================================
       ORDER GALLERY + THUMBNAILS
       (Disabled: images should stay in Shopify admin order)
    ========================================================== */

    // reorderGallery(
    //   gallery,
    //   flkty,
    //   swatchMediaIds
    // );


    // reorderThumbnails(
    //   section,
    //   swatchMediaIds
    // );

    /* ==========================================================
       MEDIA ID -> SWATCH INDEX
    ========================================================== */

    var mediaToSwatchIndex = {};


    swatchMediaIds.forEach(
      function (mediaId, index) {

        if (mediaId) {
          mediaToSwatchIndex[
            String(mediaId)
          ] = index;
        }

      }
    );


    /* ==========================================================
       FIND MEDIA INDEX
    ========================================================== */

    function findMediaIndex(mediaId) {
      if (!mediaId) {
        return -1;
      }


      var cells =
        typeof flkty.getCellElements ===
        'function'

          ? flkty.getCellElements()

          : toArray(
              gallery.querySelectorAll(
                '.product-main-slide'
              )
            );


      mediaId =
        String(mediaId);


      for (
        var i = 0;
        i < cells.length;
        i++
      ) {

        if (
          String(
            cells[i].dataset.mediaId || ''
          ) === mediaId
        ) {
          return i;
        }
      }


      return -1;
    }


    /* ==========================================================
       LOOP PROTECTION
    ========================================================== */

    var galleryUpdatingSwatch =
      false;


    /* ==========================================================
       CRITICAL PERFORMANCE FIX

       Shopify/theme code may call flkty.select()
       AFTER our color swatch change.

       That second theme call is what can re-introduce
       the long/slow slide animation.

       During a swatch change we temporarily wrap
       flkty.select() and FORCE all programmatic selections
       to be instant.

       The wrapper is removed shortly afterwards.

       Finger dragging/swiping is NOT affected.
    ========================================================== */

    var originalSelect =
      flkty.select;


    var instantSelectActive =
      false;


    var restoreSelectTimer =
      null;


    function enableInstantProgrammaticSelection() {

      if (
        instantSelectActive ||
        typeof originalSelect !== 'function'
      ) {
        return;
      }


      instantSelectActive = true;


      flkty.select =
        function (
          index,
          isWrapped,
          isInstant
        ) {

          /*
           * Force instant = TRUE.
           *
           * This catches:
           *
           * - our own selection
           * - theme's product variant media selection
           * - any immediate secondary theme selection
           */
          return originalSelect.call(
            flkty,
            index,
            isWrapped,
            true
          );
        };


      /*
       * Keep it active long enough for synchronous
       * Shopify handlers + immediate requestAnimationFrame
       * handlers to finish.
       */
      if (restoreSelectTimer) {
        clearTimeout(
          restoreSelectTimer
        );
      }


      restoreSelectTimer =
        setTimeout(
          restoreNormalSelect,
          180
        );
    }


    function restoreNormalSelect() {

      if (!instantSelectActive) {
        return;
      }


      if (restoreSelectTimer) {
        clearTimeout(
          restoreSelectTimer
        );

        restoreSelectTimer =
          null;
      }


      flkty.select =
        originalSelect;


      instantSelectActive =
        false;
    }


    /* ==========================================================
       SELECT IMAGE FOR SWATCH
    ========================================================== */

    function selectSwatchImage(
      swatchIndex
    ) {
      var mediaId =
        swatchMediaIds[
          swatchIndex
        ];


      if (!mediaId) {
        return;
      }


      var targetIndex =
        findMediaIndex(
          mediaId
        );


      if (targetIndex < 0) {
        return;
      }


      if (
        !galleryUpdatingSwatch &&
        flkty.selectedIndex !== targetIndex
      ) {

        /*
         * This will be instant while the temporary
         * select wrapper is active.
         */
        flkty.select(
          targetIndex,
          false,
          true
        );
      }


      /*
       * Dot should change immediately too.
       */
      syncDots(
        gallery,
        targetIndex
      );
    }


    /* ==========================================================
       SWATCH -> IMAGE -> DOT

       Capture phase is intentional.

       We activate instant selection BEFORE the theme receives
       its normal bubbling variant change event.
    ========================================================== */

    swatches.forEach(
      function (swatch, index) {

        swatch.addEventListener(
          'change',

          function () {

            if (
              !swatch.checked ||
              galleryUpdatingSwatch
            ) {
              return;
            }


            /*
             * Before Shopify's own variant-change handlers run,
             * temporarily force Flickity programmatic navigation
             * to be instant.
             */
            enableInstantProgrammaticSelection();


            /*
             * Move immediately to correct color image.
             */
            selectSwatchImage(
              index
            );


            /*
             * Re-confirm after Shopify's synchronous handlers.
             */
            requestAnimationFrame(
              function () {

                selectSwatchImage(
                  index
                );


                /*
                 * One more frame catches themes that perform
                 * media selection inside requestAnimationFrame.
                 */
                requestAnimationFrame(
                  function () {

                    selectSwatchImage(
                      index
                    );

                  }
                );

              }
            );

          },

          /*
           * CAPTURE PHASE.
           */
          true
        );

      }
    );


    /* ==========================================================
       GET CURRENT MEDIA
    ========================================================== */

    function getSelectedMediaId() {

      var element =
        flkty.selectedElement ||

        (
          flkty.cells &&
          flkty.cells[
            flkty.selectedIndex
          ] &&
          flkty.cells[
            flkty.selectedIndex
          ].element
        );


      if (
        !element ||
        !element.dataset.mediaId
      ) {
        return null;
      }


      return String(
        element.dataset.mediaId
      );
    }


    /* ==========================================================
       IMAGE / DOT / SWIPE -> SWATCH
    ========================================================== */

    function updateSwatchFromGallery() {

      var mediaId =
        getSelectedMediaId();


      if (!mediaId) {
        return;
      }


      var swatchIndex =
        mediaToSwatchIndex[
          mediaId
        ];


      /*
       * Box / Packaging / Lifestyle / Video:
       * don't alter selected color.
       */
      if (
        typeof swatchIndex ===
        'undefined'
      ) {
        return;
      }


      var swatch =
        swatches[
          swatchIndex
        ];


      if (
        !swatch ||
        swatch.checked
      ) {
        return;
      }


      /*
       * Prevent:
       *
       * Swipe
       * -> swatch change
       * -> gallery selection again
       */
      galleryUpdatingSwatch =
        true;


      swatch.checked =
        true;


      /*
       * Let Shopify synchronize:
       *
       * - variant ID
       * - selected color label
       * - inventory
       * - price
       * - Add To Cart
       * - URL/history
       */
      swatch.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true
          }
        )
      );


      requestAnimationFrame(
        function () {

          requestAnimationFrame(
            function () {

              galleryUpdatingSwatch =
                false;

            }
          );

        }
      );
    }


    /* ==========================================================
       FLICKITY EVENTS
    ========================================================== */

    if (
      typeof flkty.on ===
      'function'
    ) {

      /*
       * Whenever Flickity changes image,
       * immediately synchronize dot.
       */
      flkty.on(
        'select',
        function () {

          syncDots(
            gallery,
            flkty.selectedIndex
          );

        }
      );


      /*
       * Only after a real customer swipe has settled:
       * synchronize the Shopify color variant.
       */
      flkty.on(
        'settle',
        function () {

          syncDots(
            gallery,
            flkty.selectedIndex
          );


          updateSwatchFromGallery();

        }
      );
    }


    /* ==========================================================
       INITIAL STATE
    ========================================================== */

    var initialIndex =
      swatches.findIndex(
        function (swatch) {
          return swatch.checked;
        }
      );


    if (initialIndex >= 0) {

      /*
       * Initial page load is instant.
       */
      enableInstantProgrammaticSelection();


      selectSwatchImage(
        initialIndex
      );

    } else {

      syncDots(
        gallery,
        flkty.selectedIndex || 0
      );

    }


    /*
     * Some themes do one final variant/media selection
     * immediately after page initialization.
     */
    setTimeout(
      function () {

        var checkedIndex =
          swatches.findIndex(
            function (swatch) {
              return swatch.checked;
            }
          );


        if (checkedIndex >= 0) {

          enableInstantProgrammaticSelection();


          selectSwatchImage(
            checkedIndex
          );

        }

      },
      50
    );
  }


  /* ============================================================
     INITIALIZE ALL PRODUCT SECTIONS
  ============================================================ */

  function initAll(root) {
    root =
      root || document;


    var sections = [];


    if (
      root.matches &&
      root.matches(
        '.product-section[data-product-id]'
      )
    ) {
      sections.push(root);
    }


    if (root.querySelectorAll) {

      toArray(
        root.querySelectorAll(
          '.product-section[data-product-id]'
        )
      ).forEach(
        function (section) {

          if (
            sections.indexOf(section) === -1
          ) {
            sections.push(section);
          }

        }
      );
    }


    sections.forEach(
      function (section) {

        initProduct(
          section,
          0
        );

      }
    );
  }


  /* ============================================================
     NORMAL PAGE LOAD
  ============================================================ */

  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initAll(document);
      }
    );

  } else {

    initAll(document);

  }


  /* ============================================================
     SHOPIFY THEME EDITOR
  ============================================================ */

  document.addEventListener(
    'shopify:section:load',

    function (event) {
      initAll(
        event.target
      );
    }
  );


  /* ============================================================
     QUICK VIEW / AJAX PRODUCT LOAD
  ============================================================ */

  if ('MutationObserver' in window) {

    var observerQueued =
      false;


    var observer =
      new MutationObserver(
        function (mutations) {

          var foundProduct =
            false;


          for (
            var i = 0;
            i < mutations.length;
            i++
          ) {

            var nodes =
              mutations[i].addedNodes;


            for (
              var j = 0;
              j < nodes.length;
              j++
            ) {

              var node =
                nodes[j];


              if (
                !node ||
                node.nodeType !== 1
              ) {
                continue;
              }


              if (
                (
                  node.matches &&
                  node.matches(
                    '.product-section[data-product-id]'
                  )
                )

                ||

                (
                  node.querySelector &&
                  node.querySelector(
                    '.product-section[data-product-id]'
                  )
                )
              ) {

                foundProduct =
                  true;

                break;
              }
            }


            if (foundProduct) {
              break;
            }
          }


          if (
            !foundProduct ||
            observerQueued
          ) {
            return;
          }


          observerQueued =
            true;


          requestAnimationFrame(
            function () {

              observerQueued =
                false;


              initAll(
                document
              );

            }
          );

        }
      );


    observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true
      }
    );
  }

})();